import { TGetProvincesAndMunicipalitiesResDto } from "@/api/services/global/constant/dtos/get-provinces-municipalities.dto";
import { useFetchPrefectureAndMunicipalityByZipCode } from "@/api/services/global/zip-code";
import { convertZipCodeToNumber } from "@/utils/input";
import { convertFullWidthToHalfWidth } from "@/utils/pieces";
import { useEffect, useState } from "react";
import { FieldValues, Path, PathValue, UseFormReturn, useWatch } from "react-hook-form";

export type TUseLocationInfoProps<T extends FieldValues> = {
  names: {
    zipCode: Path<T>;
    province: Path<T>;
    municipality?: Path<T>;
    address?: Path<T>;
  };
  methods: UseFormReturn<T, object, undefined>;
};

export const useLocationInfo = <T extends FieldValues>({ names, methods }: TUseLocationInfoProps<T>) => {
  const { setValue } = methods;
  const zipCodeField = useWatch({ control: methods.control, name: names.zipCode });
  const [locationInfo, setLocationInfo] = useState<TGetProvincesAndMunicipalitiesResDto | undefined>(undefined);
  const [isFetching, setIsFetching] = useState(false);

  const dirtyFields = methods.formState.dirtyFields as { [key: string]: boolean };
  const dirtyZipCodeField = dirtyFields[names.zipCode];
  const [previousZipCode, setPreviousZipCode] = useState<string | undefined>();

  const { fetchByZipCode } = useFetchPrefectureAndMunicipalityByZipCode();

  const fetchLocationInfo = async (zipCode: string) => {
    setIsFetching(true);
    try {
      const rs = await fetchByZipCode(zipCode);
      setLocationInfo(rs);
      if (rs) {
        setValue(names.province, (rs.prefecture || "") as PathValue<T, Path<T>>);
        methods.trigger(names.province);
        if (names.municipality && dirtyZipCodeField) {
          setValue(names.municipality, `${rs.city || ""}` as PathValue<T, Path<T>>);
          methods.trigger(names.municipality);
        }
        if (names.address && dirtyZipCodeField) {
          setValue(names.address, `${rs?.suburb || ""}` as PathValue<T, Path<T>>);
          methods.trigger(names.address);
        }
      }
    } catch (err) {
      setValue(names.province, "" as PathValue<T, Path<T>>);
      methods.trigger(names.province);
      if (names.municipality) {
        setValue(names.municipality, "" as PathValue<T, Path<T>>);
        methods.trigger(names.municipality);
      }
      if (names.address) {
        setValue(names.address, "" as PathValue<T, Path<T>>);
        methods.trigger(names.address);
      }
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(async () => {
      const zipCode = convertZipCodeToNumber(convertFullWidthToHalfWidth(zipCodeField));
      if (zipCode?.length === 7 && zipCode !== previousZipCode) {
        setPreviousZipCode(zipCode);
        await fetchLocationInfo(zipCode);
      }
      if (zipCodeField?.length === 0) {
        setPreviousZipCode(zipCode);
        setValue(names.province, "" as PathValue<T, Path<T>>);
        methods.trigger(names.province);
        if (names.municipality) {
          setValue(names.municipality, "" as PathValue<T, Path<T>>);
          methods.trigger(names.municipality);
        }
      }
    }, 100); // Debounce 100ms fix IME

    return () => clearTimeout(handler);
  }, [zipCodeField]);

  return { locationInfo, isFetching };
};
